import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import notifications from "../../common/Components/Notifications/Notification";
import HKPConfirmDialog from "../../common/Components/HKPConfirmDialog";
import { canManageBillingSync, canUseBillingSync } from "../../helpers/billingSyncEnabled";
import {
  addZohoItemPhotos,
  chooseZohoOrganization,
  confirmZohoConnect,
  disconnectZoho,
  getZohoOrganizations,
  getZohoStatus,
  getZohoTaxes,
  refreshZohoTaxes,
  startZohoConnect,
  type ZohoStatus,
  type ZohoTaxRate,
} from "../../services/billingSyncApi";
import "./zohoBooks.scss";

const readRole = (): string | undefined => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

/** What Zoho's sign-in page sent the admin back with (?zoho=…). Only "confirm" carries a token. */
const RETURN_COPY: Record<string, { title: string; message: string }> = {
  failed: { title: "Zoho not connected", message: "The Zoho sign-in did not finish. Connect again." },
  expired: { title: "Zoho not connected", message: "The Zoho sign-in took too long. Connect again." },
  denied: { title: "Zoho not connected", message: "Access was not allowed on Zoho's page. Connect again when ready." },
};

const shortDate = (value?: string | null) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

/** Settings → Zoho Books: connect the studio's Zoho Books, pick its organisation, see GST rates and today's calls. */
const ZohoBooksPage = () => {
  const history = useHistory();
  const location = useLocation();
  const role = readRole();
  const canUse = canUseBillingSync(role);
  const canManage = canManageBillingSync(role);
  const [status, setStatus] = useState<ZohoStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState("");
  const [orgs, setOrgs] = useState<{ id: string; name: string; selected: boolean }[]>([]);
  const [chosen, setChosen] = useState("");
  const [taxes, setTaxes] = useState<{ rates: ZohoTaxRate[]; checkedAt?: string | null } | null>(null);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const handledReturn = useRef(false);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const res: any = await getZohoStatus();
      if (!res?.success) {
        setLoadError(res?.message || "Could not read the Zoho connection.");
        return;
      }
      const next: ZohoStatus = res.data;
      setStatus(next);
      if (next.status === "CHOOSE_ORG" && canManage) {
        const list: any = await getZohoOrganizations();
        if (list?.success) {
          setOrgs(list.data || []);
          setChosen((list.data || [])[0]?.id || "");
        }
      }
      if (next.connected) {
        const rates: any = await getZohoTaxes();
        if (rates?.success) setTaxes(rates.data);
      }
    } catch {
      setLoadError("Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [canManage]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!canUse) {
      // Never leave a one-time Zoho token in the address bar, even for someone who cannot use it.
      if (params.has("confirm") || params.has("zoho")) history.replace(location.pathname);
      setLoading(false);
      return;
    }
    const result = params.get("zoho");
    if (result && !handledReturn.current) {
      handledReturn.current = true;
      // Clear the address first, so a refresh never sends the one-time token again.
      history.replace(location.pathname);
      const token = params.get("confirm");
      if (result === "confirm" && token) {
        setBusy("confirm");
        confirmZohoConnect(token)
          .then((res: any) => {
            if (res?.success) {
              notifications({ title: "Zoho connected", message: res?.data?.zohoOrgName ? `Bills go to ${res.data.zohoOrgName}.` : "Choose the Zoho organisation to bill from.", type: "success" });
            } else {
              notifications({ title: "Zoho not connected", message: res?.message || "Connect again.", type: "danger" });
            }
          })
          .catch(() => notifications({ title: "Zoho not connected", message: "Check your connection and try again.", type: "danger" }))
          .finally(() => { setBusy(""); load(); });
        return;
      }
      const copy = RETURN_COPY[result];
      if (copy) notifications({ ...copy, type: "danger" });
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canUse]);

  const act = async (key: string, title: string, call: () => Promise<any>, after?: (res: any) => void) => {
    if (busy) return;
    setBusy(key);
    try {
      const res: any = await call();
      if (!res?.success) {
        notifications({ title, message: res?.message || "Try again", type: "danger" });
        return;
      }
      if (after) after(res);
      await load();
    } catch {
      notifications({ title, message: "Check your connection and try again.", type: "danger" });
    } finally {
      setBusy("");
    }
  };

  const connect = () => act("connect", "Could not open Zoho", startZohoConnect, (res) => {
    const url = res?.data?.authorizeUrl;
    if (url) window.location.assign(url);
  });

  const addPhotos = () => act("photos", "Photos not added", addZohoItemPhotos, (res) => {
    const d = res?.data || {};
    const parts = [
      d.uploaded ? `${d.uploaded} added` : "",
      d.noPhoto ? `${d.noPhoto} without a photo in HKP` : "",
      d.failed ? `${d.failed} not sent (tried again next time)` : "",
    ].filter(Boolean);
    const left = d.remaining ? ` ${d.remaining} still to do: tap Add photos again${d.stoppedBy ? " tomorrow" : ""}.` : "";
    notifications({ title: "Photos on Zoho items", message: `${parts.join(", ") || "Nothing to add"}.${left}`,
      type: d.failed || d.stoppedBy ? "warning" : "success" });
  });

  if (!canUse) {
    return (
      <div className="zb-page">
        <h2 className="zb-title">Zoho Books</h2>
        <p className="zb-muted">Zoho Books billing is not switched on for this studio.</p>
        <Link className="zb-link" to="/studio/modules">See studio modules</Link>
      </div>
    );
  }

  const state = status?.status || "NOT_CONNECTED";
  const connected = !!status?.connected;

  return (
    <div className="zb-page">
      <header className="zb-head">
        <h2 className="zb-title">Zoho Books</h2>
        <span className={`zb-chip ${connected ? "is-on" : state === "CHOOSE_ORG" ? "is-wait" : "is-off"}`}>
          {connected ? "Connected" : state === "CHOOSE_ORG" ? "Choose organisation" : "Not connected"}
        </span>
      </header>
      <p className="zb-muted">
        A Ready memo becomes a draft invoice in your Zoho Books, with the customer and each piece filled in.
        Your accountant approves it there, and the memo closes with Zoho's invoice number.
      </p>

      {loading || busy === "confirm" ? <div className="zb-card zb-muted" aria-busy="true">Reading the Zoho connection…</div> : null}
      {!loading && loadError ? (
        <div className="zb-card zb-error" role="alert">
          {loadError} <button type="button" className="zb-link" onClick={() => { setLoading(true); load(); }}>Try again</button>
        </div>
      ) : null}

      {!loading && !loadError && busy !== "confirm" && !connected && state !== "CHOOSE_ORG" ? (
        <section className="zb-card">
          <h3 className="zb-h3">Connect your Zoho Books</h3>
          <p className="zb-muted">You sign in on Zoho's own page and allow HKP to make customers, items and draft invoices. HKP never approves, sends or deletes a bill.</p>
          {canManage ? (
            <button type="button" className="zb-btn is-pri" disabled={!!busy} onClick={connect}>
              {busy === "connect" ? "Opening Zoho…" : "Connect Zoho Books"}
            </button>
          ) : (
            <p className="zb-note">Ask your studio admin to connect Zoho Books.</p>
          )}
        </section>
      ) : null}

      {!loading && !loadError && state === "CHOOSE_ORG" ? (
        <section className="zb-card">
          <h3 className="zb-h3">Which Zoho organisation bills for this studio?</h3>
          {canManage ? (
            orgs.length ? (
              <>
                <div className="zb-orgs" role="radiogroup" aria-label="Zoho organisation">
                  {orgs.map((org) => (
                    <label key={org.id} className={`zb-org${chosen === org.id ? " is-on" : ""}`}>
                      <input type="radio" name="zoho-org" checked={chosen === org.id} onChange={() => setChosen(org.id)} />
                      <span className="zb-org__name">{org.name}</span>
                      <span className="zb-mono">{org.id}</span>
                    </label>
                  ))}
                </div>
                <button type="button" className="zb-btn is-pri" disabled={!chosen || !!busy}
                  onClick={() => act("org", "Organisation not saved", () => chooseZohoOrganization(chosen))}>
                  {busy === "org" ? "Saving…" : "Use this organisation"}
                </button>
              </>
            ) : <p className="zb-muted">No organisations came back from Zoho. Connect again.</p>
          ) : (
            <p className="zb-note">Ask your studio admin to choose the Zoho organisation.</p>
          )}
        </section>
      ) : null}

      {!loading && !loadError && connected ? (
        <>
          <section className="zb-card">
            <dl className="zb-facts">
              <div><dt>Organisation</dt><dd>{status?.zohoOrgName || "—"} <span className="zb-mono">{status?.zohoOrgId}</span></dd></div>
              <div><dt>Connected on</dt><dd>{[shortDate(status?.connectedAt), status?.connectedByName].filter(Boolean).join(" · ") || "—"}</dd></div>
              <div>
                <dt>Zoho calls today</dt>
                <dd>{status?.callsToday ?? 0} of {status?.dailyCallCap ?? 900}<span className="zb-muted"> · HKP stops there to stay on Zoho's free plan</span></dd>
              </div>
            </dl>
          </section>

          <section className="zb-card">
            <div className="zb-row">
              <h3 className="zb-h3">GST rates found in Zoho</h3>
              {canManage ? (
                <button type="button" className="zb-btn" disabled={!!busy} onClick={() => act("taxes", "Taxes not read", refreshZohoTaxes)}>
                  {busy === "taxes" ? "Reading…" : "Read again"}
                </button>
              ) : null}
            </div>
            {taxes?.rates?.length ? (
              <table className="zb-table">
                <thead><tr><th scope="col">Rate</th><th scope="col">Same state (GST)</th><th scope="col">Other state (IGST)</th></tr></thead>
                <tbody>
                  {taxes.rates.map((row) => (
                    <tr key={row.rate}>
                      <td>{row.rate}%</td>
                      <td>{row.intraState ? "Found" : <span className="zb-miss">Missing in Zoho</span>}</td>
                      <td>{row.interState ? "Found" : <span className="zb-miss">Missing in Zoho</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p className="zb-muted">Not read yet. They are read the first time a memo goes to Zoho{canManage ? ", or tap Read again" : ""}.</p>
            )}
          </section>

          {typeof status?.itemPhotosMissing === "number" ? (
          <section className="zb-card">
            <div className="zb-row">
              <h3 className="zb-h3">Photos on Zoho items</h3>
              {canManage && status?.itemPhotosMissing ? (
                <button type="button" className="zb-btn" disabled={!!busy} onClick={addPhotos}>
                  {busy === "photos" ? "Adding photos…" : "Add photos"}
                </button>
              ) : null}
            </div>
            <p className="zb-muted">
              {status?.itemPhotosMissing
                ? `${status.itemPhotosMissing} ${status.itemPhotosMissing === 1 ? "item" : "items"} in Zoho ${status.itemPhotosMissing === 1 ? "has" : "have"} no photo yet.`
                : "Every item HKP made in Zoho has its photo, or the piece has none."}
              {" "}A new item gets the piece's main photo when its draft invoice is made.
            </p>
          </section>
          ) : null}

          {canManage ? (
            <button type="button" className="zb-btn is-danger" disabled={!!busy} onClick={() => setDisconnectOpen(true)}>Disconnect Zoho Books</button>
          ) : null}
        </>
      ) : null}

      <HKPConfirmDialog
        open={disconnectOpen}
        title="Disconnect Zoho Books?"
        description={`HKP stops making drafts in Zoho. Bills already in Zoho stay there. You can connect again later.${
          status?.openDrafts ? ` ${status.openDrafts === 1 ? "1 memo still has" : `${status.openDrafts} memos still have`} a draft in Zoho:`
            + " void those first. After disconnecting, each must be voided in Zoho by hand and confirmed on the memo." : ""}`}
        confirmLabel="Disconnect"
        busy={busy === "disconnect"}
        busyLabel="Disconnecting…"
        onCancel={() => setDisconnectOpen(false)}
        onConfirm={() => act("disconnect", "Not disconnected", disconnectZoho, () => {
          setDisconnectOpen(false);
          setTaxes(null);
        })}
      />
    </div>
  );
};

export default ZohoBooksPage;
